"use client";

import { useRef, useState } from "react";
import type { Media } from "@mw/shared/payload-types";
import clsx from "clsx";
import { PhoneIcon, PlayIcon, XIcon } from "lucide-react";
import { getImageUrl, getVideoUrl } from "@utilities/payload";
import { Button } from "@ui/components/core/Button";
import { ICON_ALIGN_OPTIONS } from "@ui/tokens/icon-types";
import { useTranslations } from "next-intl";
import { getLocalStorage, setLocalStorage, LocalStorageKeys } from "@utilities/localStorage";
import dynamic from "next/dynamic";

const InCall = dynamic(() => import("@ui/components/routes/conversational-ai/InlineAgent/InCall"), {
  ssr: false,
});

export interface HeroVideoAgentProps {
  agentId: string;
  backgroundVideo?: Media | null | number | string;
  callButtonText?: string | null;
  videoButtonText?: string | null;
  closeVideoButtonText?: string | null;
  agentName?: string | null;
  agentTitle?: string | null;
  agentImage?: Media | null | number | string;
  title?: string;
  description?: string | null;
}

function TermsModal({ onAgree, onClose }: { onAgree: () => void; onClose: () => void }) {
  const t = useTranslations("ClientKeys.hero_video_agent");

  return (
    <div className='bg-black/60 fixed inset-0 z-50 flex items-center justify-center px-16 backdrop-blur-sm'>
      <div
        className='relative flex w-full max-w-[460px] flex-col rounded-[24px] bg-default p-28'
        style={{
          boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.2)",
        }}
      >
        <button
          className='absolute right-16 top-16 flex h-32 w-32 items-center justify-center rounded-full text-light transition-colors hover:bg-overlay-dark hover:text-default'
          onClick={onClose}
          aria-label={t("terms.close")}
        >
          <XIcon size={18} />
        </button>

        <p className='f-paragraph-02 mb-20 pr-32 text-default'>{t("terms.intro")}</p>

        <ul className='mb-24 space-y-12'>
          <li className='f-paragraph-03 flex items-start gap-10 text-light'>
            <span className='mt-[6px] block h-5 w-5 flex-shrink-0 rounded-full bg-overlay-darker-plus' />
            <span>{t("terms.age_requirement")}</span>
          </li>
          <li className='f-paragraph-03 flex items-start gap-10 text-light'>
            <span className='mt-[6px] block h-5 w-5 flex-shrink-0 rounded-full bg-overlay-darker-plus' />
            <span>{t("terms.ai_acknowledgement")}</span>
          </li>
          <li className='f-paragraph-03 flex items-start gap-10 text-light'>
            <span className='mt-[6px] block h-5 w-5 flex-shrink-0 rounded-full bg-overlay-darker-plus' />
            <span>{t("terms.recording_consent")}</span>
          </li>
          <li className='f-paragraph-03 flex items-start gap-10 text-light'>
            <span className='mt-[6px] block h-5 w-5 flex-shrink-0 rounded-full bg-overlay-darker-plus' />
            <span>
              {t.rich("terms.privacy_policy", {
                link: chunks => (
                  <a
                    href='https://elevenlabs.io/privacy-policy'
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-default underline underline-offset-2 hover:no-underline'
                  >
                    {chunks}
                  </a>
                ),
              })}
            </span>
          </li>
          <li className='f-paragraph-03 flex items-start gap-10 text-light'>
            <span className='mt-[6px] block h-5 w-5 flex-shrink-0 rounded-full bg-overlay-darker-plus' />
            <span>{t("terms.data_sharing_consent")}</span>
          </li>
        </ul>

        <Button onClick={onAgree} variant='primary' size='lg' className='w-full'>
          {t("terms.agree_button")}
        </Button>
      </div>
    </div>
  );
}

export default function HeroVideoAgent({
  agentId,
  backgroundVideo,
  callButtonText,
  videoButtonText,
  closeVideoButtonText,
  agentName,
  agentTitle,
  agentImage,
  title,
  description,
}: HeroVideoAgentProps) {
  // TODO(agent-controller-refactor): migrate duplicated terms/session orchestration to shared controller.
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [isInCall, setIsInCall] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleVideoToggle = () => {
    if (isVideoPlaying) {
      videoRef.current?.pause();
      setIsVideoPlaying(false);
    } else {
      videoRef.current?.play();
      setIsVideoPlaying(true);
    }
  };

  const handleStartCallClick = () => {
    // Check if terms have already been accepted
    const termsAccepted = getLocalStorage(LocalStorageKeys.CONVAI_MARKETING_PAGE_TERMS);
    if (termsAccepted) {
      setIsInCall(true);
    } else {
      setShowTerms(true);
    }
  };

  const handleAgreeToTerms = () => {
    setLocalStorage(LocalStorageKeys.CONVAI_MARKETING_PAGE_TERMS, "true");
    setShowTerms(false);
    setIsInCall(true);
  };

  const handleCloseTerms = () => {
    setShowTerms(false);
  };

  const videoUrl = getVideoUrl(backgroundVideo as Media);

  return (
    <div className='relative z-[30] mb-[-2px] min-h-[600px] overflow-hidden md:min-h-[80vh]'>
      {/* Background gradient - shown while video loads or as fallback */}
      <div
        className='absolute inset-0'
        style={{
          background: "linear-gradient(180deg, #16487C 12.97%, #286190 43.13%, #4D93BB 97.27%)",
        }}
      />

      {/* Background video */}
      {videoUrl && (
        <video
          ref={videoRef}
          className='absolute inset-0 h-full w-full object-cover'
          src={videoUrl}
          playsInline
          preload='metadata'
          onEnded={() => setIsVideoPlaying(false)}
        />
      )}

      {/* Overlay for better text readability */}
      <div className='bg-black/20 absolute inset-0' />

      {/* Content */}
      <div className='container relative z-10 flex min-h-[600px] flex-col items-start py-92 md:min-h-[80vh]'>
        {/* Text Content */}
        <div className={clsx("max-w-2xl transition-opacity duration-500", isVideoPlaying && "opacity-50")}>
          {title && <h1 className='f-heading-01 mb-20 text-inverse'>{title}</h1>}
          {description && <p className='f-paragraph-01 mb-40 text-inverse-light'>{description}</p>}

          {/* Buttons */}
          <div className='flex flex-wrap items-center gap-16'>
            <Button
              onClick={handleStartCallClick}
              variant='elevated'
              size='lg'
              icon={<PhoneIcon size={16} fill='currentColor' />}
              iconPosition={ICON_ALIGN_OPTIONS.BEFORE}
              className='shadow-lg'
            >
              {callButtonText || "Start Call"}
            </Button>

            {videoUrl && (
              <Button
                onClick={handleVideoToggle}
                variant='secondary-inverse'
                size='lg'
                icon={isVideoPlaying ? <XIcon size={16} /> : <PlayIcon size={16} fill='currentColor' />}
                iconPosition={ICON_ALIGN_OPTIONS.BEFORE}
                className='backdrop-blur-sm'
              >
                {isVideoPlaying ? closeVideoButtonText || "Close video" : videoButtonText || "Watch video"}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Terms Modal - shown before starting call */}
      {showTerms && <TermsModal onAgree={handleAgreeToTerms} onClose={handleCloseTerms} />}

      {/* Agent - only shown when in call */}
      {isInCall && agentId && (
        <InCall
          agentId={agentId}
          agentName={agentName ?? undefined}
          agentTitle={agentTitle ?? undefined}
          agentImageUrl={getImageUrl(agentImage as Media)}
          onEndCall={() => setIsInCall(false)}
        />
      )}
    </div>
  );
}
